<script setup lang="ts">
import { PropType, ref } from "vue";
import ParticipantInput from "./ParticipantInput.vue";
import ParticipantsFromExcel from "./ParticipantsFromExcel.vue";
import ParticipantTable from "./ParticipantTable.vue";
import { Participant, Project } from "../../../types/PersologOnlineTypes.ts";
import { isMobileView } from "../../../utils/GeneralUtils.ts";

const props = defineProps({
    id: {
        type: String,
        required: true,
    },
    project: {
        type: Object as () => Project,
        required: true,
    },
    participantsToAdd: {
        type: Array as PropType<Participant[]>,
        default: () => [],
    },
    existingParticipants: {
        type: Array as PropType<Participant[]>,
        default: () => [],
    }
});

const emit = defineEmits(["update:valid", "update:participants", "update:duplicated"]);

const participantsToAdd = ref([...props.participantsToAdd]);
const valid = ref<boolean>(true);
const participantInputRef = ref<InstanceType<typeof ParticipantInput> | null>(null);

function  updateParticipants(newParticipants: Participant[]) {
    participantsToAdd.value = newParticipants;
    emit("update:participants", participantsToAdd.value);
}

function updateFromSingleInput(newParticipant: Participant) {
    if (newParticipant) {
        participantsToAdd.value.push(newParticipant);
        emit("update:participants", participantsToAdd.value);
    }
}

function updateValid(newValid: boolean) {
    valid.value = newValid;
    emit("update:valid", valid.value);
}

function updateDuplicated(newDuplicated: boolean) {
    valid.value = !newDuplicated;
    emit("update:duplicated", newDuplicated);
    emit("update:valid", valid.value);
}

defineExpose({
    addParticipant: () => participantInputRef.value?.addParticipant(),
});

/**
 * Returns all participants without the ones that have been deleted.
 *  */
function participantsWithoutDeleted() {
    return participantsToAdd.value?.filter((participant) => participant.status !== "DELETED");
}

/**
 * Returns the existing participants without the ones that have been deleted.
 * Deleted participants are anonymized (empty name and email) and must not be
 * shown or matched against in the dialog.
 *  */
function existingParticipantsWithoutDeleted() {
    return props.existingParticipants?.filter((participant) => participant.status !== "DELETED");
}
</script>

<template>
    <div>
        <ParticipantInput
            ref="participantInputRef"
            :participants="existingParticipantsWithoutDeleted()"
            :project="project"
            @update:participants="updateFromSingleInput"
            @update:valid="updateValid"
            @update:duplicated="updateDuplicated"
        />
        <ParticipantsFromExcel
            v-if="!isMobileView()"
            :existing-participants="existingParticipantsWithoutDeleted()"
            @update:participants="updateParticipants"
            @update:valid="updateValid"
        />
        <ParticipantTable
            :id="id"
            :project="project"
            :participants="participantsWithoutDeleted()"
            :existing-participants="existingParticipantsWithoutDeleted()"
            @update:participants="updateParticipants"
        />
    </div>
</template>
